import { App, hasActivatedProduct } from 'app/core/app/app.model';
import { isSectionEnabled } from 'app/core/environment/build-features';
import { WhatsNewAction } from './whats-new.types';

/**
 * A button in the dialog's footer, with its destination already decided.
 *
 * Resolving this *before* the dialog opens rather than in the click handler is
 * the whole point: a button that says "Open the tutorial" and lands on an
 * activation page is a promise the console does not keep. The label and the
 * destination are decided together, from the same facts.
 */
export type WhatsNewCta =
  | { kind: 'navigate'; label: string; commands: string[] }
  | { kind: 'external'; label: string; url: string };

/**
 * Label for an account with no project at all.
 *
 * Deliberately neutral: `project-creation` is not in every build's
 * `enabledSections`, so "Create a project" would be a second broken promise.
 */
export const NO_PROJECT_LABEL = 'Go to your projects';

/**
 * Decide what a call to action does for this reader, right now.
 *
 * `null` means the button has no destination worth offering in this build — the
 * caller leaves it out entirely. That only happens when the product's section
 * is switched off, in which case the announcement's documentation link is the
 * only sensible way out and it is already the secondary action.
 */
export function resolveCta(
  action: WhatsNewAction,
  apps: App[],
  activeApp: App | null,
): WhatsNewCta | null {
  const { target } = action;

  if (target.kind === 'url') {
    return { kind: 'external', label: action.label, url: target.url };
  }

  if (target.kind === 'route') {
    return { kind: 'navigate', label: action.label, commands: target.commands };
  }

  if (!isSectionEnabled(target.product)) {
    return null;
  }

  // The project the reader is looking at wins over the first one in the list:
  // being thrown into a different project than the one on screen reads as a
  // bug, even when both have the product.
  const project =
    activeApp && hasActivatedProduct(activeApp, target.product)
      ? activeApp
      : apps.find((app) => hasActivatedProduct(app, target.product));

  if (project) {
    return {
      kind: 'navigate',
      label: action.label,
      commands: ['/projects', project.id, ...target.path],
    };
  }

  // Nobody has the product yet. The button still leads somewhere useful — the
  // activation page — so it is relabelled rather than removed. The product slug
  // doubles as the route segment here, the same assumption
  // `productActivationGuard` makes when it redirects.
  const firstApp = apps[0];
  if (firstApp) {
    return {
      kind: 'navigate',
      label: target.activateLabel,
      commands: ['/projects', firstApp.id, target.product, 'activate'],
    };
  }

  return {
    kind: 'navigate',
    label: NO_PROJECT_LABEL,
    commands: ['/projects'],
  };
}
